/**
 * Statutory bill treatment policy — the single source of truth (ALC-1504).
 *
 * Every rule about how a tax guia is classified and posted lives here: the per-slot defaults, which
 * slots support AUTO_PROVISION, and the block codes. API services, the tenant fiscal-config
 * endpoints, and the web UI all read this module rather than re-deriving the decision, so the
 * classification section can never explain one entry while approval posts another.
 *
 * Design and rationale: `docs/adr/0002-statutory-bill-treatment-modes.md`.
 */

import type { TaxRevenueCodeLookup } from '../fiscal-revenue-catalog-contract.js';
import {
  DocumentType,
  StatutoryAssessmentPeriodStatus,
  StatutoryTaxComponentAttribution,
  StatutoryTaxComponentKind,
  StatutoryTaxSlotResolution,
  StatutoryTreatment,
  StatutoryTreatmentProvenance,
} from '../generated/enums.js';
import type {
  StatutoryUnmatchedReviewState,
  TaxAccrualOriginType,
  TaxLiabilityRailErrorCode,
  TaxLiabilityRailStage,
} from '../tax-liability-rail.js';
import { isStagedTaxLiabilityRailSlotCode } from '../tax-liability-rail.js';

/**
 * Default treatment per tax slot, used when the tenant has not configured one.
 *
 * The split describes how approval normally recognizes the tax, not whether missing historical
 * origin evidence blocks a known tax rail:
 *
 *  - `BYPASS` — the source event normally recognizes the liability. `*_RETIDO` withholdings are
 *    posted with the originating AP bill, payroll slots by the payroll run, and ISS slots by AR.
 *    Missing source evidence is recorded for review while approval uses the mapped rail.
 *  - `AUTO_PROVISION` — explicitly permits the guide approval to move the amount from Apurado to
 *    Em Guias a Pagar even where there is no source-origin evidence. The resulting Apurado balance
 *    may be negative and remains visible for reconciliation.
 *  - `CLASSIFY` — `ICMS_ST` is a pass-through the substitute collects from the buyer and remits.
 *    It is neither a revenue deduction nor an expense, and Alchemy ERP does not model ICMS-ST on
 *    AR, so a human picks the account rather than us inventing one.
 *
 * These are only DEFAULTS. Whether a slot is pre-provisioned depends on the taxpayer — `INSS_FOLHA`
 * is credited only if that entity's payroll runs through Alchemy ERP — so it is overridable per slot
 * (`EntityTaxSlotTreatment`, per entity). An entity with external payroll switches the payroll slots to
 * `CLASSIFY` when dimensions must be chosen manually, or `AUTO_PROVISION` to use the staged
 * Apurado → Em Guias rail and surface any missing origin as an unmatched close exception.
 *
 * `IRPJ_RECOLHER`/`CSLL_RECOLHER` are `AUTO_PROVISION` like the other own-tax slots. Their paired
 * debit is `IRPJ_EXPENSE`/`CSLL_EXPENSE` — a debit `EXPENSE` or `OTHER_EXPENSE` account, not a
 * `DEDUCAO_*` revenue deduction. The Contábil DRE presents those slots below the operating result
 * by tax-slot selection: Lei 6.404/76 art. 187 orders `IV outras receitas e despesas` then
 * `V resultado antes do IR e a provisão para o imposto`, and CPC 26 (R1) / IAS 1 require income
 * tax on its own line. CPC 32 / IAS 12 recognition still applies: the guia settles current tax,
 * so approval provisions and payment clears it.
 */
export const STATUTORY_TREATMENT_DEFAULT_BY_SLOT: Readonly<Record<string, StatutoryTreatment>> =
  Object.freeze({
    // Pre-provisioned by the originating AP bill's withholding (TaxCategory.WITHHOLDING_AP).
    IRRF_RETIDO_SERVICOS_PJ: StatutoryTreatment.BYPASS,
    IRRF_RETIDO_SERVICOS_PF: StatutoryTreatment.BYPASS,
    IRRF_RETIDO_ALUGUEIS: StatutoryTreatment.BYPASS,
    IRRF_RETIDO_ALUGUEIS_PJ: StatutoryTreatment.BYPASS,
    PIS_RETIDO: StatutoryTreatment.BYPASS,
    COFINS_RETIDO: StatutoryTreatment.BYPASS,
    CSLL_RETIDO: StatutoryTreatment.BYPASS,
    INSS_RETIDO_SERVICOS: StatutoryTreatment.BYPASS,
    ISS_RETIDO: StatutoryTreatment.BYPASS,

    // Pre-provisioned by AR invoicing / contract revenue recognition.
    ISS_PROPRIO: StatutoryTreatment.BYPASS,
    ISS_PROVISIONADO: StatutoryTreatment.BYPASS,

    // Pre-provisioned by the payroll run's provision journal.
    INSS_FOLHA: StatutoryTreatment.BYPASS,
    FGTS: StatutoryTreatment.BYPASS,
    IRRF_SALARIOS: StatutoryTreatment.BYPASS,
    CPRB_RECOLHER: StatutoryTreatment.BYPASS,
    IRRF_OUTROS_RENDIMENTOS: StatutoryTreatment.BYPASS,

    // No legal-event producer credits these upstream, so the guide approval provisions them
    // against their paired debit slot. Revenue taxes debit a `DEDUCAO_*` deduction; IRPJ/CSLL
    // debit an income-tax provision below the operating result.
    SIMPLES_NACIONAL: StatutoryTreatment.AUTO_PROVISION,
    IRPJ_RECOLHER: StatutoryTreatment.AUTO_PROVISION,
    CSLL_RECOLHER: StatutoryTreatment.AUTO_PROVISION,
    PIS_FATURAMENTO: StatutoryTreatment.AUTO_PROVISION,
    COFINS_FATURAMENTO: StatutoryTreatment.AUTO_PROVISION,
    ICMS_PROPRIO: StatutoryTreatment.AUTO_PROVISION,

    // Pass-through liability, not modeled on AR here.
    ICMS_ST: StatutoryTreatment.CLASSIFY,
  });

/**
 * Default for a slot that resolved but carries no entry above.
 *
 * `CLASSIFY` rather than `BYPASS`: a slot we have no rule for is a slot we cannot claim was
 * provisioned upstream, and `BYPASS` would silently post nothing. Full classification asks a human
 * instead of guessing. Note this is NOT the unresolvable-slot case — a guia whose slot cannot be
 * resolved at all blocks approval (see {@link StatutoryTreatmentBlockCode}) rather than landing
 * here.
 */
export const STATUTORY_TREATMENT_FALLBACK: StatutoryTreatment = StatutoryTreatment.CLASSIFY;

/** Resolve the built-in default for a slot, before any tenant configuration is applied. */
export function getDefaultStatutoryTreatment(slotCode: string): StatutoryTreatment {
  return STATUTORY_TREATMENT_DEFAULT_BY_SLOT[slotCode] ?? STATUTORY_TREATMENT_FALLBACK;
}

/**
 * Which treatments a tenant may configure for a slot.
 *
 * `AUTO_PROVISION` is available when approval has a deterministic posting path: either a legacy
 * paired debit slot or a staged tax-liability Apurado rail. On a staged rail without source
 * evidence it reclassifies the guide and leaves the missing Apurado balance visible as an
 * unmatched close exception; it does not invent tax expense.
 */
export function getConfigurableStatutoryTreatments(slot: {
  code?: string | null;
  provisionDebitSlotCode?: string | null;
}): StatutoryTreatment[] {
  return canSlotAutoProvision(slot)
    ? [StatutoryTreatment.BYPASS, StatutoryTreatment.AUTO_PROVISION, StatutoryTreatment.CLASSIFY]
    : [StatutoryTreatment.BYPASS, StatutoryTreatment.CLASSIFY];
}

/** True when the slot has a valid approval-time automatic provisioning path. */
export function canSlotAutoProvision(slot: {
  code?: string | null;
  provisionDebitSlotCode?: string | null;
}): boolean {
  const slotCode = slot.code;
  return (
    Boolean(slot.provisionDebitSlotCode) ||
    (typeof slotCode === 'string' && isStagedTaxLiabilityRailSlotCode(slotCode))
  );
}

/**
 * Why a statutory bill cannot be approved.
 *
 * Each code names exactly what is missing so the classification section can say it before the
 * approve click, and each has at least one exit the current user may be permitted to take — the
 * account-mapping link for `settings:manage` holders, and the override to `CLASSIFY` for accounting
 * policy owners with `ap:statutory-treatment:manage`. A blocker that degraded to `BYPASS` would post nothing silently; one that
 * degraded to `CLASSIFY` would decide an accounting question on the user's behalf.
 */
export const STATUTORY_TREATMENT_BLOCK_CODES = {
  HISTORICAL_ATTRIBUTION_REQUIRED: 'TAX_LIABILITY_HISTORICAL_ATTRIBUTION_REQUIRED',
  /** The document was identified as a tax guia, but its type needs a human confirmation. */
  DOCUMENT_TYPE_CONFIRMATION_REQUIRED: 'STATUTORY_DOCUMENT_TYPE_CONFIRMATION_REQUIRED',
  /** The guia's tax slot could not be resolved (unknown DARF/GPS code, missing identifier). */
  SLOT_UNRESOLVED: 'STATUTORY_SLOT_UNRESOLVED',
  /** Treatment is AUTO_PROVISION but the slot has neither a paired debit nor a staged rail. */
  PROVISION_DEBIT_SLOT_UNPAIRED: 'STATUTORY_PROVISION_DEBIT_SLOT_UNPAIRED',
  /** Legacy code retained for interpreting older failures; missing producer is now reviewable. */
  SOURCE_PRODUCER_REQUIRED: 'STATUTORY_SOURCE_PRODUCER_REQUIRED',
  /** Legacy code retained for interpreting older failures; missing origin is now reviewable. */
  SOURCE_LIABILITY_ORIGIN_REQUIRED: 'STATUTORY_SOURCE_LIABILITY_ORIGIN_REQUIRED',
  /** The taxpayer on the guia is a tenant entity other than the one the bill sits on. */
  PAYER_IS_OTHER_ENTITY: 'STATUTORY_PAYER_IS_OTHER_ENTITY',
  /**
   * The payer CNPJ/CPF read off the guia fails its check digits, so it is a misread rather than a
   * taxpayer. Matching it against the tenant's entities would find nothing and conclude
   * "third-party tax" from an OCR error — silently expensing the tenant's own tax — so the guia
   * waits for the payer to be corrected (reprocess) or for an explicit classification.
   */
  PAYER_TAX_ID_INVALID: 'STATUTORY_PAYER_TAX_ID_INVALID',
  /**
   * A slot in the auto-provision entry is not mapped to an account in the tenant's CoA version.
   *
   * Surfaced when the bill is OPENED, not only when approval fails: the approver sees the missing
   * account up front, with the mapping link if they hold `settings:manage` and the protected override
   * to CLASSIFY. Approval itself fails with the resolver's own
   * `TAX_ACCOUNT_SLOT_NOT_FOUND` / `TAX_ACCOUNT_NOT_MAPPED`, which already carry the recovery link.
   */
  PROVISION_ACCOUNT_NOT_MAPPED: 'STATUTORY_PROVISION_ACCOUNT_NOT_MAPPED',
  /**
   * A tax liability that this guia will clear on payment is not mapped. This applies to both
   * already-provisioned and approval-time-provisioned taxes: in either case payment would have no
   * liability account to debit.
   */
  PAYMENT_SETTLEMENT_ACCOUNT_NOT_MAPPED: 'STATUTORY_PAYMENT_SETTLEMENT_ACCOUNT_NOT_MAPPED',
  /** No period covers the bill's competency date, so no CoA version can be resolved. */
  NO_PERIOD_FOR_COMPETENCY_DATE: 'STATUTORY_NO_PERIOD_FOR_COMPETENCY_DATE',
  /**
   * The entity has no BR_CPC book, so an AUTO_PROVISION has nowhere to post. Approval would
   * silently post nothing (`NO_APPLICABLE_BR_CPC_BOOK`), so the preview blocks up front instead of
   * naming accounts from a period of some other framework's book.
   */
  NO_APPLICABLE_BR_CPC_BOOK: 'STATUTORY_NO_APPLICABLE_BR_CPC_BOOK',
  /**
   * A multi-line guia mixes `CLASSIFY` lines with lines that settle an existing tax liability.
   *
   * There is no single bill-level treatment that is correct for such a guia, and picking one
   * double-counts (accounting review ACC-01): treating the whole bill as `CLASSIFY` creates an AP
   * payable and an expense for the FULL amount, so the already-provisioned line is expensed a second
   * time and its original liability is never cleared. Treating it as `BYPASS`/`AUTO_PROVISION` instead
   * leaves the genuinely classifiable line with no expense at all. The guia has to be split, or the
   * treatment chosen deliberately.
   */
  MIXED_TREATMENT_UNSUPPORTED: 'STATUTORY_MIXED_TREATMENT_UNSUPPORTED',
} as const;

export type StatutoryTreatmentBlockCode =
  (typeof STATUTORY_TREATMENT_BLOCK_CODES)[keyof typeof STATUTORY_TREATMENT_BLOCK_CODES];

/** The only document types a user may confirm for a statutory payment guia. */
export const STATUTORY_DOCUMENT_TYPE_OPTIONS = [
  DocumentType.DARF,
  DocumentType.GPS,
  DocumentType.GUIA_ISS,
  DocumentType.GUIA_ICMS,
  DocumentType.GRF,
  DocumentType.DAS,
] as const;

export type StatutoryDocumentTypeOption = (typeof STATUTORY_DOCUMENT_TYPE_OPTIONS)[number];

/** Why the extracted identity cannot be used without a human decision. */
export type StatutoryDocumentTypeConfirmationReason =
  'MISSING_EXTRACTION' | 'AMBIGUOUS_EXTRACTION' | 'TYPE_MISMATCH';

/** Server-owned prompt for confirming a tax guia type before it may be approved. */
export interface StatutoryDocumentTypeConfirmation {
  documentId: string | null;
  /** Attached documents that the user may select when no single source can be chosen safely. */
  candidateDocumentIds?: readonly string[];
  /** True when approval is blocked until a type is selected. False permits correcting a prior choice. */
  isRequired: boolean;
  reason: StatutoryDocumentTypeConfirmationReason;
  persistedDocumentType: string | null;
  extractedDocumentType: string | null;
  confirmedDocumentType: StatutoryDocumentTypeOption | null;
  options: readonly StatutoryDocumentTypeOption[];
}

/**
 * How the payer tax ID on the guia was matched against the tenant's entities.
 *
 * Payer identity DOMINATES the slot rule rather than merely informing it: if the taxpayer is a third
 * party then no event in the tenant's books provisioned the tax, whatever the slot says. An
 * `ISS_PROPRIO` guia in a client's CNPJ was never provisioned by the tenant's AR invoice.
 */
export const STATUTORY_PAYER_MATCH = {
  /** Exact hit on `Entity.cnpj` or an ACTIVE `EntityTaxIdentifier` of the bill's entity. */
  OWN_ENTITY_EXACT: 'OWN_ENTITY_EXACT',
  /**
   * 8-char CNPJ root hit. An unregistered filial's guia is genuinely the tenant's own
   * pre-provisioned tax, and exact-only matching would push it into classification every month. The
   * root identifies the company, so an unrelated client cannot share it — but the branch is flagged
   * for review.
   */
  OWN_ENTITY_ROOT: 'OWN_ENTITY_ROOT',
  /** Exact or root hit on a DIFFERENT tenant entity — a mis-assigned bill, not a third-party tax. */
  OTHER_TENANT_ENTITY: 'OTHER_TENANT_ENTITY',
  /** Read confidently and matched nothing: the tenant is paying someone else's tax. */
  THIRD_PARTY: 'THIRD_PARTY',
  /**
   * No usable payer tax ID on the guia — fall through to the slot rule. A full CNPJ/CPF that fails
   * its check digits is also UNKNOWN, but blocks with `STATUTORY_PAYER_TAX_ID_INVALID`.
   */
  UNKNOWN: 'UNKNOWN',
  /**
   * A user declared the guia is the bill entity's own tax without fixing the read payer (the
   * misread digit is not legible, or the guia prints a payer the entity pays for itself). Skips
   * payer matching and the invalid-tax-id block; the slot rule decides, as for any own tax.
   */
  OWN_ENTITY_DECLARED: 'OWN_ENTITY_DECLARED',
} as const;

export type StatutoryPayerMatch =
  (typeof STATUTORY_PAYER_MATCH)[keyof typeof STATUTORY_PAYER_MATCH];

/** True when the guia's taxpayer is the bill's own entity — matched by tax ID or declared. */
export function isOwnEntityPayerMatch(match: StatutoryPayerMatch | null | undefined): boolean {
  return (
    match === STATUTORY_PAYER_MATCH.OWN_ENTITY_EXACT ||
    match === STATUTORY_PAYER_MATCH.OWN_ENTITY_ROOT ||
    match === STATUTORY_PAYER_MATCH.OWN_ENTITY_DECLARED
  );
}

/**
 * A human decision about who the guia's taxpayer is, stored on the bill (never on the extraction,
 * which stays the AI's raw read). It sits directly below the explicit treatment override in the
 * precedence chain of ADR 0002.
 *
 * - `CORRECTED_TAX_ID`: the read payer CNPJ/CPF was wrong; `taxId` (check digits verified) replaces
 *   it for matching, so every existing payer rule still applies to the corrected value.
 * - `OWN_ENTITY`: the guia is the bill entity's own tax; `reason` is required for the audit trail.
 */
export const STATUTORY_PAYER_DECLARATION_KINDS = {
  CORRECTED_TAX_ID: 'CORRECTED_TAX_ID',
  OWN_ENTITY: 'OWN_ENTITY',
} as const;

export type StatutoryPayerDeclarationKind =
  (typeof STATUTORY_PAYER_DECLARATION_KINDS)[keyof typeof STATUTORY_PAYER_DECLARATION_KINDS];

export interface StatutoryPayerDeclaration {
  kind: StatutoryPayerDeclarationKind;
  /** Corrected payer tax ID, normalized; set only for `CORRECTED_TAX_ID`. */
  taxId: string | null;
  /** The payer tax ID the extraction read when the declaration was made, normalized. */
  readTaxId: string | null;
  /** Why the user declared it; required for `OWN_ENTITY`. */
  reason: string | null;
  declaredByUserId: string | null;
  /** ISO timestamp. */
  declaredAt: string;
}

/** Stable error codes of the payer declaration endpoints. */
export const STATUTORY_PAYER_DECLARATION_ERROR_CODES = {
  TAX_ID_INVALID: 'STATUTORY_PAYER_CORRECTION_TAX_ID_INVALID',
  TAX_ID_UNCHANGED: 'STATUTORY_PAYER_CORRECTION_TAX_ID_UNCHANGED',
  REASON_REQUIRED: 'STATUTORY_PAYER_DECLARATION_REASON_REQUIRED',
} as const;

/** Minimum length of the reason an `OWN_ENTITY` declaration must carry. */
export const STATUTORY_PAYER_DECLARATION_REASON_MIN_LENGTH = 5;

/** Body of `PUT /bills/:billId/statutory-treatment/payer`. */
export interface SetStatutoryPayerDeclarationRequest {
  kind: StatutoryPayerDeclarationKind;
  /** Required for `CORRECTED_TAX_ID`: the CNPJ/CPF printed on the guia, any punctuation. */
  taxId?: string | null;
  /** Required for `OWN_ENTITY` (min. 5 characters); optional note for a correction. */
  reason?: string | null;
}

/** One already-approved guia whose treatment the current rule would decide differently. */
export interface StatutoryTreatmentDivergenceItem {
  billId: string;
  entityId: string;
  vendorName: string | null;
  description: string | null;
  billCompetencyDate: string | null;
  totalAmount: number;
  /** What the bill was actually approved and posted under. */
  persistedTreatment: StatutoryTreatment;
  /** What the current rule would decide for the same guia. */
  derivedTreatment: StatutoryTreatment;
  derivedSlotCode: string | null;
  derivedReason: string;
  payerMatch: StatutoryPayerMatch;
  payerName: string | null;
  /** Set when the current rule would not have been able to decide at all. */
  blockCode: StatutoryTreatmentBlockCode | null;
  /**
   * Plain-language consequence of the divergence, so finance can triage without reading the code.
   */
  impact: string;
}

/**
 * Read-only diagnostic: approved guias the new rule would treat differently (ALC-1504).
 *
 * The migration is behaviour-preserving, so nothing was rewritten — at the affected tenants these are
 * prior-period misstatements (a missing expense, a dangling liability debit), and correcting them is an
 * accounting exercise rather than a migration. This report exists so finance can decide case by case.
 */
export interface StatutoryTreatmentDivergenceReport {
  /** Approved statutory bills examined. */
  examined: number;
  /** How many of those diverge. */
  diverged: number;
  /** Bills whose treatment could not be re-derived (missing extraction) and were skipped. */
  skipped: number;
  items: StatutoryTreatmentDivergenceItem[];
}

/** One tax slot's effective statutory treatment for an entity, for the fiscal settings screen. */
export interface EntityTaxSlotTreatmentItem {
  slotCode: string;
  slotName: string;
  /** User-facing explanation of the tax and which Alchemy module provisions it, when any. */
  slotDescription: string | null;
  category: string;
  /** Legacy paired debit slot; staged tax-liability rails can auto-provision without one. */
  provisionDebitSlotCode: string | null;
  canAutoProvision: boolean;
  /** Treatments accepted by the data contract for this slot. */
  allowedTreatments: StatutoryTreatment[];
  /** The built-in default, so the UI can show what is inherited rather than chosen. */
  defaultTreatment: StatutoryTreatment;
  /** The effective treatment: the configured value, or the default when unconfigured. */
  treatment: StatutoryTreatment;
  isConfigured: boolean;
  updatedAt: string | null;
  updatedBy: string | null;
}

export interface EntityTaxSlotTreatmentListResponse {
  entityId: string;
  /** This CPC-specific configuration is available when at least one entity book uses BR_CPC. */
  isAvailable: boolean;
  /** Distinct accounting frameworks used by the entity's books, for unavailable-state copy. */
  accountingFrameworkCodes: string[];
  items: EntityTaxSlotTreatmentItem[];
}

/** One account leg of the entry an AUTO_PROVISION bill will post, for the UI to name it. */
export interface StatutoryProvisionAccountPreview {
  /** Account identifier used by the constrained selector when the mapping may be edited. */
  accountId: string | null;
  slotCode: string;
  /** GL account code, or null when the slot is not mapped for this CoA version. */
  accountCode: string | null;
  accountName: string | null;
}

export interface StatutoryAccountBalancePreview {
  bookId: string;
  bookName: string;
  currency: string;
  /** Credit-positive balance for the liability account across POSTED journals. */
  balanceAmount: number;
  /** Latest POSTED journal date affecting the displayed liability account in the book. */
  latestMovementDate: string | null;
}

/** One principal / fine / interest component of a persisted statutory tax line (ALC-1540). */
export interface StatutoryNormalizedTaxComponent {
  kind: (typeof StatutoryTaxComponentKind)[keyof typeof StatutoryTaxComponentKind];
  amount: number;
  attribution: (typeof StatutoryTaxComponentAttribution)[keyof typeof StatutoryTaxComponentAttribution];
}

/** Frozen audit evidence for one book in the staged FGTS liability lifecycle (ALC-1541). */
export interface StatutoryStagedLiabilityEvidence {
  bookId: string;
  bookName: string;
  functionalCurrency: string;
  logicalRailCode: string;
  sourceStage: TaxLiabilityRailStage;
  destinationStage: TaxLiabilityRailStage;
  approvalVersionId: string;
  approvalApuradoAccount: StatutoryProvisionAccountPreview;
  approvalGuidesPayableAccount: StatutoryProvisionAccountPreview;
  settlementVersionId: string;
  settlementApuradoAccount: StatutoryProvisionAccountPreview;
  settlementGuidesPayableAccount: StatutoryProvisionAccountPreview;
  approvalJournalId: string;
  /** Accrual journal that created the Apurado origin for staged AUTO_PROVISION lines. */
  accrualJournalId: string | null;
  matchedAmount: number;
  unmatchedAmount: number;
  /** Ordinary Em Guias a Pagar principal clearing for this tax line (ALC-1542). */
  paidAmount: number;
  /** Fine expense cleared with the ordinary payment, when present (ALC-1542). */
  paidFineAmount: number;
  /** Interest expense cleared with the ordinary payment, when present (ALC-1542). */
  paidInterestAmount: number;
  /** Cleared Em Guias principal later restored by payment reversal evidence (ALC-1542). */
  reversedAmount: number;
  /** Remaining approved guide-stage principal still awaiting payment (ALC-1542). */
  remainingAmount: number;
  /** Active payment clearing journal for this book, when settled. */
  paymentJournalId: string | null;
  /** Linked payment-reversal journal for this book, when payment was reversed. */
  paymentReversalJournalId: string | null;
  /** Explicit unmatched-principal review state for ALC-1543 self-heal. */
  reviewState: StatutoryUnmatchedReviewState;
  reviewRequestId: string | null;
  frozenAt: string;
  origins: {
    originId: string;
    sourceEventType: TaxAccrualOriginType;
    sourceEventId: string;
    sourceJournalId: string;
    amount: number;
  }[];
}

/** Approval-time staged accounts shown before evidence is frozen (ALC-1541). */
interface StatutoryStagedLiabilityPlanBase {
  bookId: string;
  bookName: string;
  sourceStage: TaxLiabilityRailStage;
  destinationStage: TaxLiabilityRailStage;
}

export interface ReadyStatutoryStagedLiabilityPlan extends StatutoryStagedLiabilityPlanBase {
  readiness: 'READY';
  blockerCode: null;
  blockerMessage: null;
  approvalVersionId: string;
  approvalApuradoAccount: StatutoryProvisionAccountPreview;
  /** Pre-rail liability balance retained under the managed parent, when this rail was wrapped. */
  approvalLegadoAccount: StatutoryProvisionAccountPreview | null;
  approvalGuidesPayableAccount: StatutoryProvisionAccountPreview;
}

export interface BlockedStatutoryStagedLiabilityPlan extends StatutoryStagedLiabilityPlanBase {
  readiness: 'BLOCKED';
  blockerCode:
    | 'NO_OPEN_PERIOD'
    | 'NO_SCHEDULED_PAYMENT_DATE'
    | 'NO_SETTLEMENT_PERIOD'
    | 'STAGED_RAIL_NOT_CONFIGURED'
    | 'STAGED_SETTLEMENT_RAIL_NOT_CONFIGURED'
    | 'STAGED_SETTLEMENT_CROSSWALK_MISSING'
    | 'STAGED_SETTLEMENT_OVERRIDE_INVALID'
    | TaxLiabilityRailErrorCode;
  blockerMessage: string;
  approvalVersionId: string | null;
  approvalApuradoAccount: null;
  approvalGuidesPayableAccount: null;
}

/** One readiness result is returned for every applicable BR_CPC book. */
export type StatutoryStagedLiabilityPlan =
  ReadyStatutoryStagedLiabilityPlan | BlockedStatutoryStagedLiabilityPlan;

/** Eligible source accrual offered for pre-approval manual selection (ALC-1554). */
export interface StatutoryEligibleOriginPreview {
  originId: string;
  bookId: string;
  bookName: string;
  sourceEventType: TaxAccrualOriginType;
  sourceEventId: string;
  sourceJournalId: string;
  availableAmount: number;
  functionalCurrency: string;
  assessmentPeriodYear: number;
  assessmentPeriodMonth: number;
  recognizedAt: string;
}

/**
 * Privileged one-off correction for a single statutory tax line (ALC-1554).
 *
 * Never mutates entity Tratamento de Guia or system-account mapping defaults. Slot-only confirmation
 * accepts an optional reason; other overrides require one. All changes persist audit history.
 */
export interface OverrideStatutoryTaxLineRequest {
  /** Confirm a bill-local liability slot for an unrecognized tax; never a global code mapping. */
  resolvedSlotCode?: string;
  /** When set, replaces the line treatment with a bill-local BILL_OVERRIDE. */
  treatment?: StatutoryTreatment;
  /** When true, clears a prior line treatment override and re-derives from slot/entity policy. */
  clearTreatmentOverride?: boolean;
  /**
   * One-off settlement GL account for this line. Pass `null` to clear a prior override and fall
   * back to the mapped system account without changing that mapping.
   */
  settlementGlAccountId?: string | null;
  /**
   * Restrict approval allocation to these eligible origins (same entity/book/slot/period rules).
   * Pass `null` to restore automatic FIFO across all eligible origins.
   */
  selectedOriginIds?: string[] | null;
  /** Optional for slot-only confirmation; mandatory for other privileged changes. */
  reason?: string;
}

/** One extracted charge and the exact statutory rail that will handle it. */
export interface StatutoryTreatmentLinePreview {
  /** Official code identity; does not establish a GL mapping or authorize provisioning. */
  revenueCodeReference?: TaxRevenueCodeLookup;
  /** Unrecognized-tax suggestion only; approval remains blocked until a user confirms a slot. */
  suggestedSlotCode?: string | null;
  /** All eligible payable tax obligations; reviewed candidates are marked and ordered first. */
  slotResolutionOptions?: { code: string; name: string; suggested?: boolean }[];
  /** The liability slot was deliberately selected for this unrecognized source line. */
  slotResolvedManually?: boolean;
  /**
   * Durable identity of the persisted normalized tax line (ALC-1540).
   * Null only when the compatibility path has not yet synced lines for this bill.
   */
  taxLineId: string | null;
  taxCode: string | null;
  taxDescription: string | null;
  principalAmount: number;
  /** Source guide currency for line-level display. */
  currency: string;
  fineAmount: number;
  interestAmount: number;
  slotId: string | null;
  slotCode: string | null;
  slotName: string | null;
  slotDescription: string | null;
  slotResolution:
    (typeof StatutoryTaxSlotResolution)[keyof typeof StatutoryTaxSlotResolution] | null;
  treatment: StatutoryTreatment;
  treatmentProvenance:
    (typeof StatutoryTreatmentProvenance)[keyof typeof StatutoryTreatmentProvenance] | null;
  /** Line-level rationale (derived or privileged override reason). */
  treatmentReason: string | null;
  allowedTreatments: StatutoryTreatment[];
  liabilityAccount: StatutoryProvisionAccountPreview | null;
  /**
   * Version/book scope that produced `liabilityAccount` and that inline remaps
   * must write to. Null when the line spans multiple posting versions and the UI
   * must choose a book-specific mapping path instead of entity-only fallback.
   */
  liabilityAccountVersionId: string | null;
  liabilityAccountBookId: string | null;
  /** True when `liabilityAccount` comes from a one-off bill-line override (ALC-1554). */
  settlementAccountOverridden: boolean;
  balances: StatutoryAccountBalancePreview[];
  /** Canonical assessment year; null when unresolved / ambiguous / unsupported. */
  assessmentPeriodYear: number | null;
  assessmentPeriodMonth: number | null;
  assessmentPeriodStatus:
    (typeof StatutoryAssessmentPeriodStatus)[keyof typeof StatutoryAssessmentPeriodStatus] | null;
  assessmentPeriodRaw: string | null;
  components: StatutoryNormalizedTaxComponent[];
  /** ISO timestamp when the line was frozen at final approval; null while still editable. */
  frozenAt: string | null;
  /** False once the line is frozen or the bill is past pre-approval statuses. */
  canEdit: boolean;
  /** Pre-approval eligible origins for manual selection (ALC-1554). */
  eligibleOrigins: StatutoryEligibleOriginPreview[];
  /** Selected origin ids when a privileged user restricted automatic FIFO (ALC-1554). */
  selectedOriginIds: string[];
  /** Intended per-book approval transfer, visible before final approval. */
  stagedLiabilityPlan: StatutoryStagedLiabilityPlan[];
  /** Present after final approval for staged FGTS BYPASS lines. */
  stagedLiabilityEvidence: StatutoryStagedLiabilityEvidence[];
}

/** Legacy preview shape; supported BR_CPC AUTO lines now show source-backed reclassification. */
export interface StatutoryProvisionLinePreview {
  slotCode: string;
  taxCode: string | null;
  principalAmount: number;
  debit: StatutoryProvisionAccountPreview;
  credit: StatutoryProvisionAccountPreview;
}

/**
 * Everything the bill's classification section needs to explain what approval will do — and, when it
 * cannot, exactly what is missing and which exits the current user may take.
 *
 * The UI renders this rather than re-deriving the decision, so the explanation and the posting can
 * never disagree.
 */
export interface StatutoryTreatmentStatusResponse {
  accountingFallback?: { reviewRequestId: string; reason: string } | null;
  /** Bill-scoped recovery actions for ambiguous shared liability evidence. */
  liabilityReviewScopes?: { bookId: string; railParentAccountId: string }[];
  /** True when the bill came from a statutory document at all. */
  isFromStatutoryDocument: boolean;
  /** The persisted treatment — the one approval will act on. */
  treatment: StatutoryTreatment;
  /** True when a user deliberately chose CLASSIFY; suppresses the recompute at approval. */
  isOverridden: boolean;
  slotCode: string | null;
  reason: string | null;
  payerMatch: StatutoryPayerMatch | null;
  payerName: string | null;
  payerTaxId: string | null;
  /**
   * The payer tax ID exactly as the extraction read it — differs from `payerTaxId` once a user
   * corrected it. Null when the guia printed none.
   */
  payerReadTaxId?: string | null;
  /** The bill's active payer declaration (correction or own-tax claim), shown with an undo. */
  payerDeclaration?: StatutoryPayerDeclaration | null;
  /**
   * True when the bill holds a payer correction but the guia, read again, now yields that same
   * CNPJ/CPF — the correction no longer changes anything and can be undone. The UI warns; nothing
   * is cleared automatically, because the correction is a human decision on record.
   */
  payerDeclarationRedundant?: boolean;
  /** Set when approval is blocked. */
  blockCode: StatutoryTreatmentBlockCode | null;
  /** Free-text detail for the blocker, naming the missing slot or account. */
  blockDetail: string | null;
  /** The tax slot whose mapping is missing, when that is the blocker. */
  blockedSlotCode: string | null;
  /** An unregistered-branch root match worth a human glance. */
  requiresBranchReview: boolean;
  /** Entity the guia belongs to, when the bill is assigned to the wrong one. */
  payerEntityId: string | null;
  /** The exact entry approval will post; empty unless the treatment is AUTO_PROVISION. */
  provisionPreview: StatutoryProvisionLinePreview[];
  /** Total principal the provision will book. */
  provisionTotalAmount: number;
  /**
   * Date the provision is recognized at (ISO yyyy-mm-dd): for an AUTO_PROVISION, the last day of
   * the tax lines' período de apuração, which is exactly what approval posts on (forward-clamped
   * only when that period has closed). Other treatments fall back to `billCompetencyDate`.
   *
   * Shown because it is not the approval date: a June DAS approved in July still books into June.
   */
  provisionCompetencyDate: string | null;
  /** Entity-scoped rule owner; treatment edits affect this entity, not just this bill. */
  entityId: string | null;
  /** Every extracted tax line, including BYPASS lines that approval does not post. */
  lines: StatutoryTreatmentLinePreview[];
  /** False once the bill is past the pre-approval statuses where treatment may still change. */
  canOverride: boolean;
  /** Present only when a statutory document identity needs a human confirmation. */
  documentTypeConfirmation?: StatutoryDocumentTypeConfirmation | null;
  /**
   * Set when the derived treatment no longer matches the persisted one.
   *
   * The ADR requires the change to be refreshed AND re-surfaced rather than silently posted, so this
   * shows on bill open instead of only failing the approve click with STATUTORY_TREATMENT_CHANGED. Null
   * when they agree, or when the bill is user-overridden (a decision, not a stale computation).
   */
  staleTreatment: StatutoryTreatment | null;
}

/** The derived treatment of a statutory bill, or the reason it cannot be approved. */
export interface StatutoryTreatmentResolution {
  treatment: StatutoryTreatment;
  /** Resolved tax slot, `DARF_MULTI` for a multi-line DARF, or null when unresolvable. */
  slotCode: string | null;
  /** How the payer tax ID matched, so the UI can say why classification is being asked for. */
  payerMatch: StatutoryPayerMatch;
  /** Taxpayer name read off the guia, for the CLASSIFY copy. */
  payerName: string | null;
  /** Taxpayer tax ID read off the guia, normalized. */
  payerTaxId: string | null;
  /** Set when approval must be blocked; `treatment` is then the mode that could not be honoured. */
  blockCode: StatutoryTreatmentBlockCode | null;
  /** Human-readable audit trail persisted on the bill and shown to the approver. */
  reason: string;
  /** True when an unregistered-branch root match should be flagged for review. */
  requiresBranchReview: boolean;
  /** The tenant entity the guia belongs to, when `blockCode` is PAYER_IS_OTHER_ENTITY. */
  payerEntityId?: string | null;
  /** The payer tax ID the extraction read, before any declaration replaced it. */
  payerReadTaxId?: string | null;
  /** The bill's payer declaration the resolution honoured, if any. */
  payerDeclaration?: StatutoryPayerDeclaration | null;
}

/**
 * Treatments that keep the statutory rails: classification UI hidden, no AP payable, and payment
 * posting `Dr <tax liability> / Cr Bank` through the clearing service.
 *
 * This is the predicate every consumer that used to read `isFromStatutoryDocument` for BEHAVIOUR
 * must use. A `CLASSIFY` guia is an ordinary bill from the payment path's point of view: it has a
 * real payable that must clear normally. Routing it into statutory clearing would give it a
 * spurious tax-liability debit AND leave its payable uncleared.
 */
export function isStatutoryTreatmentOnRails(
  treatment: StatutoryTreatment | null | undefined,
): boolean {
  return treatment === StatutoryTreatment.BYPASS || treatment === StatutoryTreatment.AUTO_PROVISION;
}

/** True when approval must post the auto-provision entry rather than a normal bill provision. */
export function isStatutoryAutoProvision(
  treatment: StatutoryTreatment | null | undefined,
): boolean {
  return treatment === StatutoryTreatment.AUTO_PROVISION;
}

/** Bill types whose GL lands at reconciliation instead of at approval. */
const RECONCILIATION_SHORTCUT_BILL_TYPES = [
  'BANK_FEE',
  'VENDOR_ADVANCEMENT',
  'CREDIT_CARD_INVOICE',
];

/**
 * What approval posts for a bill.
 *
 * Replaces the old boolean `skipBillProvisionGl`, which could only say "normal provision" or
 * "nothing" and so had no way to express `AUTO_PROVISION` — a mode that must post, just not the
 * normal provision. Routing `AUTO_PROVISION` through `postBillToGl` would post
 * `Dr <classified expense> / Cr AP payable` using a classification the UI deliberately never
 * collected, and would put a tax-authority obligation into Fornecedores.
 *
 * Maintenance contract: read `docs/adr/0002-statutory-bill-treatment-modes.md` before changing this
 * router. Its outputs are consumed under the locked approval row and are coupled to statutory
 * provisioning, payment clearing, classification bypass, close controls, and reversal.
 */
export type BillProvisionMode =
  /** `postBillToGl`: Dr classified expense / Cr AP payable. */
  | 'NORMAL'
  /** Dr Tax/Apurado / Cr Tax/Em Guias a Pagar, principal only, at final approval. */
  | 'STATUTORY_BYPASS_RECLASSIFICATION'
  /** Dr paired debit slot / Cr tax liability slot, principal only, at the competency date. */
  | 'STATUTORY_AUTO_PROVISION'
  /** Nothing at approval; GL lands at payment reconciliation. */
  | 'NONE';

export function resolveBillProvisionMode(bill: {
  billType?: string | null;
  isFromStatutoryDocument?: boolean | null;
  statutoryTreatment?: StatutoryTreatment | null;
}): BillProvisionMode {
  if (bill.billType && RECONCILIATION_SHORTCUT_BILL_TYPES.includes(bill.billType)) {
    return 'NONE';
  }
  if (bill.isFromStatutoryDocument !== true) {
    return 'NORMAL';
  }
  // A statutory bill with no treatment loaded keeps the pre-ALC-1504 behaviour (post nothing), so a
  // partial select can never turn a bypass into a spurious AP provision.
  switch (bill.statutoryTreatment ?? StatutoryTreatment.BYPASS) {
    case StatutoryTreatment.BYPASS:
      return 'STATUTORY_BYPASS_RECLASSIFICATION';
    case StatutoryTreatment.AUTO_PROVISION:
      return 'STATUTORY_AUTO_PROVISION';
    default:
      return 'NORMAL';
  }
}
